import { da } from 'date-fns/locale';
import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { AuthService } from 'services/AuthService';
import axios from 'axios';
import { baseUrl } from '../utils/config';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  error: string | null;
  validateAndRefreshToken: () => Promise<boolean>;
  hasValidTokens: () => boolean;
  isTokenExpired: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Admin credentials - in a real app, these would be stored securely on the server
// and validated through an API call
const ADMIN_USERNAME = 'admin123';
const ADMIN_PASSWORD = 'SkinAI2025';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [refreshFailed, setRefreshFailed] = useState<boolean>(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0);

  // Refs to ensure axios interceptors see up-to-date values
  const isRefreshingRef = useRef<boolean>(false);
  const refreshFailedRef = useRef<boolean>(false);
  const lastRefreshTimeRef = useRef<number>(0);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => { isRefreshingRef.current = isRefreshing; }, [isRefreshing]);
  useEffect(() => { refreshFailedRef.current = refreshFailed; }, [refreshFailed]);
  useEffect(() => { lastRefreshTimeRef.current = lastRefreshTime; }, [lastRefreshTime]);

  // Centralized token refresh routine used by both proactive checks and interceptors
  const beginTokenRefresh = async (existingRefreshToken: string): Promise<void> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    setIsRefreshing(true);
    isRefreshingRef.current = true;

    const refreshOperation = (async () => {
      const response = await AuthService.refreshToken(existingRefreshToken);
      const newAccessToken = response.access_token;
      const newRefreshToken = response.refresh_token;

      // Store both new tokens
      localStorage.setItem('auth_token', newAccessToken);
      localStorage.setItem('refresh_token', newRefreshToken);

      // Update authentication state
      setIsAuthenticated(true);

      // Update last refresh time
      const nowTs = Date.now();
      setLastRefreshTime(nowTs);
      lastRefreshTimeRef.current = nowTs;

      // Clear refresh failed flag on success
      setRefreshFailed(false);
      refreshFailedRef.current = false;
    })()
      .catch((err) => {
        setRefreshFailed(true);
        refreshFailedRef.current = true;
        logout();
        throw err;
      })
      .finally(() => {
        setIsRefreshing(false);
        isRefreshingRef.current = false;
        refreshPromiseRef.current = null;
      });

    refreshPromiseRef.current = refreshOperation;
    return refreshOperation;
  };

  // Function to check if user has valid tokens
  const hasValidTokens = (): boolean => {
    const accessToken = localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('refresh_token');
    return !!(accessToken && refreshToken);
  };

  // Function to check if current token is expired
  const isTokenExpired = (): boolean => {
    const accessToken = localStorage.getItem('auth_token');
    if (!accessToken) return true;

    try {
      const tokenPayload = decodeJWT(accessToken);
      if (!tokenPayload || !tokenPayload.exp) return true;

      const currentTime = Math.floor(Date.now() / 1000);
      return currentTime >= tokenPayload.exp;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  };

  // Function to safely decode JWT token
  const decodeJWT = (token: string): any => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  };

    // Function to validate token and refresh if needed
  const validateAndRefreshToken = async (): Promise<boolean> => {
    const accessToken = localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (!accessToken) {
      return false;
    }

    // Prevent multiple simultaneous refresh attempts
    if (isRefreshingRef.current) {
      console.log('⚠️ AuthContext: Token refresh already in progress, skipping...');
      return true;
    }

    // If refresh has already failed, don't try again
    if (refreshFailedRef.current) {
      console.log('⚠️ AuthContext: Refresh already failed, logging out user');
      logout();
      return false;
    }

    // Check if token is expired or will expire soon (with 2 minutes buffer)
    if (isTokenExpired() || isTokenExpiredSoon()) {
      // Check if refresh should be attempted
      if (!shouldAttemptRefresh()) {
        console.log('⚠️ AuthContext: Cannot attempt refresh, logging out user');
        setRefreshFailed(true);
        logout();
        return false;
      }

      // Token is expired or will expire soon, try to refresh
      if (refreshToken) {
        try {
          console.log('🔄 AuthContext: Token expired, attempting refresh...');
          await beginTokenRefresh(refreshToken);
          return true;
        } catch (refreshError) {
          console.error('❌ AuthContext: Failed to refresh token:', refreshError);
          // Mark refresh as failed to prevent further attempts
          setRefreshFailed(true);
          refreshFailedRef.current = true;
          // Refresh failed, clear tokens and logout
          logout();
          return false;
        }
      } else {
        // No refresh token, logout
        logout();
        return false;
      }
    }
    
    // Token is still valid
    return true;
  };

  // Function to check if token will expire soon (adaptive based on token lifetime)
  const isTokenExpiredSoon = (): boolean => {
    const accessToken = localStorage.getItem('auth_token');
    if (!accessToken) return true;

    try {
      const tokenPayload = decodeJWT(accessToken);
      if (!tokenPayload || !tokenPayload.exp) return true;

      const currentTime = Math.floor(Date.now() / 1000);
      const { bufferMinutes } = getOptimalRefreshTiming();
      const bufferSeconds = bufferMinutes * 60;
      
      return currentTime >= (tokenPayload.exp - bufferSeconds);
    } catch (error) {
      console.error('Error checking if token expires soon:', error);
      return true;
    }
  };

  // Function to check if refresh token is expired
  const isRefreshTokenExpired = (): boolean => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return true;

    try {
      const tokenPayload = decodeJWT(refreshToken);
      if (!tokenPayload || !tokenPayload.exp) return true;

      const currentTime = Math.floor(Date.now() / 1000);
      return currentTime >= tokenPayload.exp;
    } catch (error) {
      console.error('Error checking refresh token expiration:', error);
      return true;
    }
  };

  // Function to get optimal refresh timing based on token lifetime
  const getOptimalRefreshTiming = (): { checkInterval: number; cooldownPeriod: number; bufferMinutes: number } => {
    const accessToken = localStorage.getItem('auth_token');
    if (!accessToken) {
      return { checkInterval: 5 * 60 * 1000, cooldownPeriod: 2 * 60 * 1000, bufferMinutes: 5 };
    }

    try {
      const tokenPayload = decodeJWT(accessToken);
      if (!tokenPayload || !tokenPayload.exp) {
        return { checkInterval: 5 * 60 * 1000, cooldownPeriod: 2 * 60 * 1000, bufferMinutes: 5 };
      }

      const tokenLifetimeMinutes = Math.floor((tokenPayload.exp - (tokenPayload.iat || Math.floor(Date.now() / 1000))) / 60);
      
      // Adaptive timing based on token lifetime
      if (tokenLifetimeMinutes <= 5) {
        // Very short tokens (1-5 minutes): check every 30 seconds, 1 minute cooldown, 1 minute buffer
        return { checkInterval: 30 * 1000, cooldownPeriod: 60 * 1000, bufferMinutes: 1 };
      } else if (tokenLifetimeMinutes <= 15) {
        // Short tokens (5-15 minutes): check every 2 minutes, 1 minute cooldown, 2 minute buffer
        return { checkInterval: 2 * 60 * 1000, cooldownPeriod: 60 * 1000, bufferMinutes: 2 };
      } else if (tokenLifetimeMinutes <= 60) {
        // Medium tokens (15-60 minutes): check every 5 minutes, 2 minute cooldown, 5 minute buffer
        return { checkInterval: 5 * 60 * 1000, cooldownPeriod: 2 * 60 * 1000, bufferMinutes: 5 };
      } else {
        // Long tokens (60+ minutes): check every 10 minutes, 5 minute cooldown, 10 minute buffer
        return { checkInterval: 10 * 60 * 1000, cooldownPeriod: 5 * 60 * 1000, bufferMinutes: 10 };
      }
    } catch (error) {
      console.error('Error calculating optimal refresh timing:', error);
      return { checkInterval: 5 * 60 * 1000, cooldownPeriod: 2 * 60 * 1000, bufferMinutes: 5 };
    }
  };

  // Function to check if refresh should be attempted
  const shouldAttemptRefresh = (): boolean => {
    // Don't attempt refresh if already refreshing or if refresh has failed
    if (isRefreshingRef.current || refreshFailedRef.current) {
      return false;
    }

    // Don't attempt refresh if refresh token is expired
    if (isRefreshTokenExpired()) {
      return false;
    }

    // Don't attempt refresh if we just refreshed recently (adaptive cooldown)
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current;
    const { cooldownPeriod } = getOptimalRefreshTiming();
    if (timeSinceLastRefresh < cooldownPeriod) {
      console.log('⚠️ Refresh cooldown: Last refresh was', Math.round(timeSinceLastRefresh / 1000), 'seconds ago');
      return false;
    }

    return true;
  };

  // Attach axios interceptor once
  useEffect(() => {
    const requestInterceptorId = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const url = config.url || '';
      const isAuthFree = url.includes('/loginTachafy') || url.includes('/refresh-token');
      if (token && !isAuthFree) {
        config.headers = config.headers || {};
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
      return config;
    });

    const responseInterceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // If the failing request is the refresh endpoint itself, do NOT try to refresh again
        const failingUrl: string | undefined = error?.config?.url;
        if (failingUrl && failingUrl.includes('/refresh-token')) {
          console.log('⚠️ Axios interceptor: Refresh endpoint failed, skipping further refresh attempts and logging out');
          setRefreshFailed(true);
          refreshFailedRef.current = true;
          logout();
          return Promise.reject(error);
        }

        // Prevent infinite loops by checking if this request is already a refresh attempt
        if (error.config._retry) {
          console.log('⚠️ Axios interceptor: Request already retried, not attempting refresh again');
          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          // Prevent multiple simultaneous refresh attempts
          if (isRefreshingRef.current) {
            console.log('⚠️ Axios interceptor: Token refresh already in progress, queueing request until it completes...');
            if (refreshPromiseRef.current) {
              try {
                await refreshPromiseRef.current;
                const newTokenAfterWait = localStorage.getItem('auth_token');
                if (newTokenAfterWait) {
                  error.config._retry = true;
                  error.config.headers = error.config.headers || {};
                  error.config.headers.Authorization = `Bearer ${newTokenAfterWait}`;
                  return axios.request(error.config);
                }
              } catch (e) {
                return Promise.reject(e);
              }
            }
            return Promise.reject(error);
          }

          // If refresh has already failed, don't try again
          if (refreshFailedRef.current) {
            console.log('⚠️ Axios interceptor: Refresh already failed, logging out user');
            logout();
            return Promise.reject(error);
          }

          // Check if this is a refresh token error (from backend)
          const errorDetail = error.response?.data?.detail;
          if (errorDetail === 'REFRESH_TOKEN_EXPIRED' || errorDetail === 'REFRESH_TOKEN_INVALID') {
            console.log('⚠️ Axios interceptor: Refresh token is expired/invalid, logging out user');
            setRefreshFailed(true);
            logout();
            return Promise.reject(error);
          }

          // Check if refresh should be attempted
          if (!shouldAttemptRefresh()) {
            console.log('⚠️ Axios interceptor: Cannot attempt refresh, logging out user');
            setRefreshFailed(true);
            refreshFailedRef.current = true;
            logout();
            return Promise.reject(error);
          }

          console.log('🔄 Axios interceptor: 401 response, attempting token refresh...');
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              await beginTokenRefresh(refreshToken);

              const newToken = localStorage.getItem('auth_token');
              if (!newToken) {
                return Promise.reject(error);
              }

              console.log('✅ Axios interceptor: Tokens refreshed, retrying request...');

              // Mark this request as retried to prevent infinite loops
              error.config._retry = true;
              
              // Retry the original request with the new token
              error.config.headers = error.config.headers || {};
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return axios.request(error.config);
            } catch (refreshError) {
              console.error('❌ Axios interceptor: Token refresh failed:', refreshError);
              // Mark refresh as failed to prevent further attempts
              setRefreshFailed(true);
              refreshFailedRef.current = true;
              // Refresh failed, logout user and stop retrying
              logout();
              return Promise.reject(refreshError);
            }
          } else {
            console.log('⚠️ Axios interceptor: No refresh token available, logging out user');
            // No refresh token, logout user
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, []);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  // Update authentication state when tokens change
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('auth_token');
      setIsAuthenticated(!!token);
    };

    const handleLogin = () => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (token) {
        setIsAuthenticated(true);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('login', handleLogin);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('login', handleLogin);
    };
  }, []);

  // Proactive token refresh - check and refresh tokens before they expire
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkAndRefreshTokens = async () => {
      // Log current refresh configuration
      const { checkInterval, cooldownPeriod, bufferMinutes } = getOptimalRefreshTiming();
      console.log('📊 Refresh config: Check every', Math.round(checkInterval / 1000 / 60), 'min, Cooldown', Math.round(cooldownPeriod / 1000 / 60), 'min, Buffer', bufferMinutes, 'min');
      
      // Only refresh if token will expire soon, we haven't refreshed recently, and cooldown has passed
      if (isTokenExpiredSoon() && shouldAttemptRefresh()) {
        console.log('🔄 Proactive token refresh: Access token expiring soon');
        await validateAndRefreshToken();
      } else {
        // Log why refresh wasn't attempted
        if (isTokenExpiredSoon()) {
          if (isRefreshing) {
            console.log('⏳ Proactive refresh skipped: Already refreshing');
          } else if (refreshFailed) {
            console.log('⏳ Proactive refresh skipped: Refresh failed previously');
          } else if (isRefreshTokenExpired()) {
            console.log('⏳ Proactive refresh skipped: Refresh token expired');
          } else {
            const timeSinceLastRefresh = Date.now() - lastRefreshTime;
            console.log('⏳ Proactive refresh skipped: Cooldown active (last refresh was', Math.round(timeSinceLastRefresh / 1000), 'seconds ago)');
          }
        }
      }
    };

    // Adaptive check interval based on token lifetime
    const { checkInterval } = getOptimalRefreshTiming();
    const interval = setInterval(checkAndRefreshTokens, checkInterval);
    
    // Don't check immediately to avoid unnecessary refreshes on login
    const initialCheck = setTimeout(checkAndRefreshTokens, checkInterval);

    return () => {
      clearInterval(interval);
      clearTimeout(initialCheck);
    };
  }, [isAuthenticated, validateAndRefreshToken, isRefreshing]);

  const login = async (username: string, password: string): Promise<boolean> => {
    setError(null);
    // Reset refresh failed flag on new login
    setRefreshFailed(false);
    // Set initial refresh time
    setLastRefreshTime(Date.now());
    
    // addition by alaa 
    const data = await AuthService.login(username, password);
    if (data && data.token && data.user.role === 'admin') {
      // Token is already stored in AuthService.login
      localStorage.setItem('user', JSON.stringify(data.user));
      // Store the admin's email in localStorage so profile/settings pages can fetch the connected admin's info
      localStorage.setItem('userEmail', username); // <-- Add this line
      setIsAuthenticated(true);
      return true;
    } else {
      setError('Invalid username or password');
      return false;
    }
  };

  const logout = () => {
    // Hard reset: wipe all localStorage entries
    try {
      localStorage.clear();
    } catch {}
    // Remove any default Authorization header
    try { delete (axios.defaults.headers as any).common['Authorization']; } catch {}
    // Notify listeners and update auth state
    window.dispatchEvent(new Event('logout'));
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, error, validateAndRefreshToken, hasValidTokens, isTokenExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 